import type { EstadoCampanaEnvio, ResultadosCampanaPublic } from '@app/shared';
import { Check } from 'lucide-react';

import { useI18n } from '../../../i18n/I18nProvider';
import { cn } from '../../../lib/cn';

// ciclo-emisor, parte 2: las piezas chicas de la pantalla de Campañas.

/** Varias opciones tildables como botones (los filtros). Ninguna tildada = no filtra. */
export function GrupoOpciones<T extends string>({
  opciones,
  elegidas,
  onChange,
  etiqueta,
  deshabilitado = false,
}: {
  opciones: readonly T[];
  elegidas: T[];
  onChange: (v: T[]) => void;
  etiqueta: (v: T) => string;
  deshabilitado?: boolean;
}): JSX.Element {
  const alternar = (v: T): void =>
    onChange(elegidas.includes(v) ? elegidas.filter((x) => x !== v) : [...elegidas, v]);
  return (
    <div className="flex flex-wrap gap-2">
      {opciones.map((v) => {
        const on = elegidas.includes(v);
        return (
          <button
            key={v}
            type="button"
            disabled={deshabilitado}
            aria-pressed={on}
            onClick={() => alternar(v)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60',
              on
                ? 'border-marca-primario bg-marca-primario text-white'
                : 'border-marca-neutro bg-surface text-marca-gris hover:bg-marca-neutro',
            )}
          >
            {on && <Check size={14} />}
            {etiqueta(v)}
          </button>
        );
      })}
    </div>
  );
}

const TONO_ESTADO: Record<EstadoCampanaEnvio, string> = {
  BORRADOR: 'bg-surface-muted text-text-subtle',
  ACTIVA: 'bg-success/15 text-success',
  PAUSADA: 'bg-marca-amarillo/20 text-text-strong',
  TERMINADA: 'bg-marca-azul/15 text-marca-azul',
};

export function SelloEstado({ estado }: { estado: EstadoCampanaEnvio }): JSX.Element {
  const t = useI18n().campanasEnvio;
  return (
    <span className={cn('rounded-full px-2.5 py-0.5 text-xs font-semibold', TONO_ESTADO[estado])}>
      {t.estados[estado]}
    </span>
  );
}

/** Lo que pasó DESPUÉS de los envíos (lo que faltó en AstroYoga), en grande o en línea. */
export function Resultados({
  r,
  compacto = false,
}: {
  r: ResultadosCampanaPublic;
  compacto?: boolean;
}): JSX.Element {
  const t = useI18n().campanasEnvio.resultados;
  const principales: { etiqueta: string; valor: number; ayuda?: string; alerta?: boolean }[] = [
    { etiqueta: t.enviados, valor: r.enviados },
    { etiqueta: t.entregados, valor: r.entregados },
    { etiqueta: t.respondieron, valor: r.respondieron },
    { etiqueta: t.avanzaron, valor: r.avanzaron },
    { etiqueta: t.compraron, valor: r.compraron },
    {
      etiqueta: t.noEntregados,
      valor: r.noEntregados,
      ayuda: t.noEntregadosAyuda,
      alerta: r.noEntregados > 0,
    },
  ];
  if (compacto) {
    return (
      <p className="text-sm text-text-subtle">
        {principales.slice(2).map((p, i) => (
          <span key={p.etiqueta} title={p.ayuda} className={cn(p.alerta && 'text-danger')}>
            {i > 0 && ' · '}
            {p.etiqueta} <strong className="tabular-nums text-text-strong">{p.valor}</strong>
          </span>
        ))}
      </p>
    );
  }
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-6 gap-3">
        {principales.map((p) => (
          <div
            key={p.etiqueta}
            title={p.ayuda}
            className="rounded-xl border border-border-subtle bg-surface p-4 shadow-card"
          >
            <div className="text-xs uppercase tracking-wide text-text-subtle">{p.etiqueta}</div>
            <div
              className={cn(
                'mt-1 text-3xl font-bold tabular-nums text-text-strong',
                p.alerta && 'text-danger',
              )}
            >
              {p.valor}
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-text-faint">
        {t.pendientes} {r.pendientes} · {t.sinWhatsApp} {r.sinWhatsApp} · {t.salteados} {r.salteados}
      </p>
    </div>
  );
}
